import React, { useEffect, useMemo, useState } from 'react';
import { HiCheck } from 'react-icons/hi';
import { HiOutlineExternalLink } from 'react-icons/hi';
import Postrio from '../assets/postrio.jpg';
import Areascouts from '../assets/areascouts.png';
import Silhouetteandshadow from '../assets/sil.png';
import MirconetBD from '../assets/micronetbd.png';
import PropertySourceAU from '../assets/propertysourceau.png';
import AmponPH from '../assets/amponph.png';
import FamilyFirst from '../assets/familyfirst.png';
import TheLinkChurch from '../assets/thelinkchurch.png';
import HeyDentalWebsite from '../assets/hey-dental-website.jpg';
import InvoiceFollowUp from '../assets/invoice-follow-up-system.png';
import LeadConversionEngine from '../assets/lead-conversion-engine.png';

const automationCaseStudies = [
  {
    id: 'invoice-follow-up',
    outcomeHeadline:
      'Accounts receivable follow-up that runs itself—daily sync from Sheets to email with zero manual chasing.',
    title: 'Invoice Follow-Up System',
    subtitle: 'Smarter payment reminders—without the busywork',
    image: InvoiceFollowUp,
    imageAlt:
      'Automation workflow: daily schedule, Google Sheets, routing by overdue tier, Gmail reminders, and sheet updates',
    problem:
      'Teams lose time and consistency chasing unpaid invoices by hand, and it is hard to see who was contacted and when.',
    solution:
      'An automated daily run that reads invoices from Sheets, routes follow-ups by how overdue they are, emails clients, and writes status back—no manual chasing.',
    keyFeatures: [
      'Daily scheduled runs so follow-up happens without someone remembering to click',
      'Tiered reminders (including escalation) tied to how late each invoice is',
      'Sheet + Gmail stay in sync so finance always has a clear paper trail',
    ],
    techStack: ['n8n', 'Google Sheets', 'Gmail', 'Scheduled runs', 'Custom routing logic'],
    result:
      'Guaranteed daily follow-ups with no manual chasing—finance keeps full visibility in the sheet.',
  },
  {
    id: 'lead-conversion-engine',
    outcomeHeadline:
      'Automated growth engine: from first click to nurtured lead—no manual CRM copy-paste or dropped handoffs.',
    title: 'Lead Conversion Engine',
    subtitle: 'From first click to nurtured follow-up—without the manual handoffs',
    image: LeadConversionEngine,
    imageAlt:
      'n8n workflow: webhook lead capture, lead qualification, HubSpot contact upsert, Gmail, Slack, and timed email sequence',
    problem:
      'Leads arrive from the web but responses, CRM updates, and nurture steps are uneven—so opportunities cool off.',
    solution:
      'A single workflow that ingests each lead, qualifies it, syncs HubSpot, notifies the team on Slack, and runs timed email follow-ups automatically.',
    keyFeatures: [
      'Webhook intake from forms or any system that can POST lead data',
      'Hot vs. standard routing plus HubSpot upsert so sales works from one source of truth',
      'Instant email + Slack alert, then scheduled follow-ups until the sequence completes',
    ],
    techStack: ['n8n', 'Webhooks', 'HubSpot', 'Gmail', 'Slack', 'Wait & schedule steps'],
    result:
      'Faster first response, no manual CRM entry, and a repeatable path from lead to conversation.',
  },
];

const webProjects = [
  {
    id: 'propertysourceau',
    title: 'The Property Source AU System',
    subtitle: 'Real estate SaaS—listings, payments, and admin in one place',
    image: PropertySourceAU,
    imageAlt: 'The Property Source AU platform preview',
    category: 'Systems',
    href: 'https://thepropertysource.com.au/',
    overview:
      'A real estate SaaS product for the Australian market: one place to run listings, take payments, and manage operations—with secure access for the right people.',
    highlights: [
      'End-to-end platform for listings, Stripe payments, and back-office admin',
      'Integrations and role-based access so teams and partners use one system',
      'Shipped for production use on web and mobile-friendly layouts',
    ],
    techStack: ['Ruby on Rails', 'Stripe', 'Tailwind', 'Google Services'],
  },
  {
    id: 'amponph',
    title: 'AmponPH',
    subtitle: 'Dog adoption platform connecting rescuers with future adopters',
    image: AmponPH,
    imageAlt: 'AmponPH dog adoption platform preview',
    category: 'Systems',
    href: 'https://www.amponph.com/',
    overview:
      'A work-in-progress platform focused on helping dog rescuers and people looking to adopt connect in one place, with a smoother path from discovery to adoption.',
    highlights: [
      'Built around matching adoptable dogs with people ready to give them a home',
      'Designed to support rescuers with a cleaner way to list dogs and manage inquiries',
      'Currently in progress, with the core adoption browsing experience already taking shape',
    ],
    techStack: ['Next.js', 'Supabase', 'Node.js'],
  },
  {
    id: 'areascouts-app',
    title: 'Area Scouts Platform',
    subtitle: 'Athlete Development Program & whole-athlete assessments',
    image: Areascouts,
    imageAlt: 'Area Scouts platform preview',
    category: 'Systems',
    href: 'https://app.areascouts.com/',
    overview:
      'The digital home for Area Scouts—a youth sports development organization. The app presents programs, assessments, and resources so families and coaches can understand offerings and engage with training and whole-athlete development in one place (Ruby on Rails).',
    highlights: [
      'Supports athlete development programs and related assessments from a single platform',
      'Built for real families, athletes, and staff—not a brochure, a working product',
      'Positions the brand as full youth development, not only on-field training',
    ],
    techStack: ['Ruby on Rails', 'Youth sports & assessments', 'Production SaaS'],
  },
  {
    id: 'silhouetteandshadow',
    title: 'Silhouette & Shadow Portal',
    subtitle: 'Live drawing on Zoom—community, calendar, and member artwork',
    image: Silhouetteandshadow,
    imageAlt: 'Silhouette & Shadow community and sessions preview',
    category: 'Systems',
    href: 'https://silhouetteandshadow.org/',
    overview:
      'A community platform for live virtual figure drawing: members discover sessions, join on Zoom, and stay connected to a shared calendar of classes and events—so the organization can run recurring programming without juggling spreadsheets.',
    highlights: [
      'Purpose-built for live creative sessions and community scheduling',
      'Supports members and guests who want to register and attend online',
      'Keeps the brand, calendar, and session access in one cohesive experience',
    ],
    techStack: ['Community platform', 'Session & calendar', 'Member & guest checkout', 'Zoom-ready flows'],
  },
  {
    id: 'familyfirst',
    title: 'Family First Dental',
    subtitle: 'Marketing site for a dental practice',
    image: FamilyFirst,
    imageAlt: 'Family First Dental website preview',
    category: 'Websites',
    href: 'https://family-first-dental.vercel.app/',
    overview:
      'A marketing site that helps a dental practice present services clearly and earn trust before the first visit—fast on Vercel and easy to use on phones.',
    highlights: [
      'Positions the practice for new patients with clear services and contact paths',
      'Healthcare-appropriate tone and layout',
      'Lightweight delivery for quick loads and updates',
    ],
    techStack: ['Next.js', 'Vercel', 'Responsive UI'],
  },
  {
    id: 'hey-dental-website',
    title: 'Hey Dental',
    subtitle: 'Brand-forward site for a Netherlands dental practice',
    image: HeyDentalWebsite,
    imageAlt: 'Hey Dental website preview',
    category: 'Websites',
    href: 'https://heydental.nl/',
    overview:
      'Hey Dental’s public-facing site: brand, services, and booking-oriented content for patients in the Netherlands—optimized for clarity and mobile browsing.',
    highlights: [
      'NL-market messaging and strong brand presence',
      'Built to turn visitors into appointment requests',
      'Performance-minded, mobile-first layout',
    ],
    techStack: ['Next.js', 'International / NL', 'Performance'],
  },
  {
    id: 'thelinkchurch',
    title: 'The Link Church',
    subtitle: 'Church organization website—community and events',
    image: TheLinkChurch,
    imageAlt: 'The Link Church website preview',
    category: 'Websites',
    href: 'https://www.thelinkphilippines.com/',
    overview:
      'The church’s main web hub: events, ministries, and community information in a clear structure so newcomers and members find what matters quickly.',
    highlights: [
      'Community-first content hierarchy',
      'Event and ministry discovery without clutter',
      'Readable, accessible presentation on all screen sizes',
    ],
    techStack: ['Vue.js', 'Content structure', 'Responsive'],
  },
  {
    id: 'micronetbd',
    title: 'MicronetBD',
    subtitle: 'Organization website—mission and programs',
    image: MirconetBD,
    imageAlt: 'MicronetBD website preview',
    category: 'Websites',
    href: 'https://micronetbd.org/',
    overview:
      'An organization site that puts mission and programs first—so partners and supporters understand who you are and how to engage, on any device.',
    highlights: [
      'Nonprofit-focused story and impact framing',
      'Simple paths for partners and donors',
      'Responsive, lightweight pages',
    ],
    techStack: ['WordPress', 'Nonprofit', 'Responsive'],
  },
  {
    id: 'postrio',
    title: 'Postrio',
    subtitle: 'Product marketing site',
    image: Postrio,
    imageAlt: 'Postrio website preview',
    category: 'Websites',
    href: 'https://www.postrio.io/',
    overview:
      'A product marketing site that explains what Postrio does, why it matters, and what to do next—built to support a growing brand without noise.',
    highlights: [
      'Clear product story and value proposition',
      'Conversion paths for interested prospects',
      'Cohesive visual system as the product evolves',
    ],
    techStack: ['HTML', 'CSS', 'Bootstrap'],
  },
];

/** Dispatched from hero CTAs so the correct filter is active after scroll. */
export const PORTFOLIO_WORK_FILTER_EVENT = 'portfolio-work-filter';

const Work = () => {
  const [activeFilter, setActiveFilter] = useState('All');

  useEffect(() => {
    const valid = ['All', 'Websites', 'Systems', 'Automation'];
    const handler = (e) => {
      const filter = e.detail;
      if (valid.includes(filter)) setActiveFilter(filter);
    };
    window.addEventListener(PORTFOLIO_WORK_FILTER_EVENT, handler);
    return () => window.removeEventListener(PORTFOLIO_WORK_FILTER_EVENT, handler);
  }, []);

  const categoryCounts = useMemo(() => {
    const base = webProjects.reduce((acc, p) => {
      acc[p.category] = (acc[p.category] || 0) + 1;
      return acc;
    }, {});
    base.Automation = automationCaseStudies.length;
    return base;
  }, []);

  const systemProjects = useMemo(
    () => webProjects.filter((project) => project.category === 'Systems'),
    []
  );

  const websiteProjects = useMemo(
    () => webProjects.filter((project) => project.category === 'Websites'),
    []
  );

  const categories = useMemo(() => {
    const unique = Object.keys(categoryCounts);
    const ordered = ['Systems', 'Websites', 'Automation'].filter((c) => unique.includes(c));
    return ['All', ...ordered];
  }, [categoryCounts]);

  const totalCount = webProjects.length + automationCaseStudies.length;

  const renderProjectCards = (projects) => (
    <div className='space-y-12'>
      {projects.map((project) => (
        <article
          key={project.id}
          className='rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden bg-gray-50/50 dark:bg-[#0f172a]/50'
        >
          <div className='grid md:grid-cols-2 gap-0'>
            <div className='aspect-[4/3] md:aspect-auto md:min-h-[260px] bg-white dark:bg-[#112240] p-4 flex items-center justify-center overflow-hidden group'>
              <img
                src={project.image}
                alt={project.imageAlt}
                className='w-full h-full max-h-[320px] object-contain object-center rounded-md transition-transform duration-500 ease-out group-hover:scale-105'
                loading='lazy'
              />
            </div>
            <div className='p-6 md:p-8 flex flex-col justify-center'>
              <h4 className='text-2xl font-bold text-gray-900 dark:text-[#ccd6f6]'>{project.title}</h4>
              <p className='text-pink-600 dark:text-pink-400 font-medium mt-1'>{project.subtitle}</p>
              {project.href ? (
                <a
                  href={project.href}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='mt-6 inline-flex items-center gap-2 self-start px-4 py-2.5 text-sm font-semibold rounded-lg bg-pink-600 text-white hover:bg-pink-500 transition-colors'
                >
                  View live site
                  <HiOutlineExternalLink className='text-lg' aria-hidden />
                </a>
              ) : null}
            </div>
          </div>
          <div className='p-6 md:p-8 border-t border-gray-200 dark:border-gray-700'>
            <h5 className='text-xs font-semibold uppercase tracking-wider text-pink-600 mb-3'>What we built</h5>
            <p className='text-gray-700 dark:text-gray-300 text-sm leading-relaxed'>{project.overview}</p>
          </div>
          <div className='px-6 md:px-8 pb-8 grid gap-8 lg:grid-cols-2'>
            <div>
              <h5 className='text-xs font-semibold uppercase tracking-wider text-pink-600 mb-3'>
                At a glance
              </h5>
              <ul className='space-y-2'>
                {project.highlights.map((item) => (
                  <li key={item} className='flex gap-2 text-sm text-gray-700 dark:text-gray-300'>
                    <HiCheck className='text-pink-600 shrink-0 mt-0.5' aria-hidden />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h5 className='text-xs font-semibold uppercase tracking-wider text-pink-600 mb-3'>Tech stack</h5>
              <ul className='flex flex-wrap gap-2'>
                {project.techStack.map((tech) => (
                  <li
                    key={tech}
                    className='text-xs font-medium px-3 py-1 rounded-full bg-white dark:bg-[#112240] border border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-200'
                  >
                    {tech}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </article>
      ))}
    </div>
  );

  const renderProjectSection = ({ title, description, projects, ariaLabel, className = '' }) => (
    <section aria-label={ariaLabel} className={className}>
      <h3 className='text-sm font-semibold uppercase tracking-widest text-pink-600 mb-2'>{title}</h3>
      <p className='text-gray-600 dark:text-[#8892b0] mb-8 max-w-2xl'>{description}</p>
      {renderProjectCards(projects)}
    </section>
  );

  return (
    <div
      name='case-studies'
      className='w-full bg-white text-gray-900 dark:bg-[#0a192f] dark:text-gray-300 py-24'
    >
      <div className='max-w-[1000px] mx-auto p-4 flex flex-col justify-center w-full h-full'>
        <div className='pb-6'>
          <h2 className='text-5xl font-bold inline border-b-4 text-gray-900 dark:text-gray-300 border-pink-600'>
            Case studies
          </h2>
          <p className='py-4 text-gray-600 dark:text-[#8892b0] max-w-2xl'>
            Process-first: workflow diagrams and business outcomes before UI polish—so you see the engine, not just the
            brochure.
          </p>
        </div>

        <div className='flex flex-wrap gap-3 mb-10'>
          {categories.map((category) => (
            <button
              key={category}
              type='button'
              onClick={() => setActiveFilter(category)}
              className={`px-4 py-2 border-2 text-sm font-medium transition-colors ${activeFilter === category
                ? 'bg-pink-600 border-pink-600 text-white'
                : 'border-gray-300 text-gray-700 hover:bg-pink-600 hover:border-pink-600 hover:text-white dark:border-gray-500 dark:text-gray-300 dark:hover:text-white'
                }`}
            >
              {category}
              {category === 'All'
                ? ` (${totalCount})`
                : ` (${categoryCounts[category] || 0})`}
            </button>
          ))}
        </div>

        {activeFilter === 'All' && (
          <>
            {renderProjectSection({
              title: 'Systems',
              description: 'Apps and internal tools with structured workflows, business logic, and dependable delivery.',
              projects: systemProjects,
              ariaLabel: 'Systems case studies',
              className: 'mb-16',
            })}

            {renderProjectSection({
              title: 'Websites',
              description: 'Marketing and brochure sites that are clear, fast, and built to convert.',
              projects: websiteProjects,
              ariaLabel: 'Website case studies',
              className: 'mb-16',
            })}
          </>
        )}

        {activeFilter === 'Systems' &&
          renderProjectSection({
            title: 'Systems',
            description: 'Apps and internal tools with structured workflows, business logic, and dependable delivery.',
            projects: systemProjects,
            ariaLabel: 'Systems case studies',
          })}

        {activeFilter === 'Websites' &&
          renderProjectSection({
            title: 'Websites',
            description: 'Marketing and brochure sites that are clear, fast, and built to convert.',
            projects: websiteProjects,
            ariaLabel: 'Website case studies',
          })}

        {(activeFilter === 'All' || activeFilter === 'Automation') && (
          <section
            className={activeFilter === 'All' ? '' : 'mb-16'}
            aria-label='Automation case studies'
          >
            <h3 className='text-sm font-semibold uppercase tracking-widest text-pink-600 mb-2'>
              Automation &amp; workflows (n8n)
            </h3>
            <p className='text-gray-600 dark:text-[#8892b0] mb-8 max-w-2xl'>
              Real pipelines: triggers, routing, CRM/email/Slack, and scheduled follow-ups—documented by outcome, not buzzwords.
            </p>
            <div className='space-y-12'>
              {automationCaseStudies.map((study) => (
                <article
                  key={study.id}
                  className='rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden bg-gray-50/50 dark:bg-[#0f172a]/50'
                >
                  <div className='grid md:grid-cols-2 gap-0'>
                    <div className='aspect-[4/3] md:aspect-auto md:min-h-[280px] bg-white dark:bg-[#112240] p-4 flex items-center justify-center overflow-hidden group'>
                      <img
                        src={study.image}
                        alt={study.imageAlt}
                        className='w-full h-full max-h-[340px] object-contain object-center rounded-md transition-transform duration-500 ease-out group-hover:scale-105'
                        loading='lazy'
                      />
                    </div>
                    <div className='p-6 md:p-8 flex flex-col justify-center'>
                      <p className='text-base sm:text-lg font-semibold text-gray-900 dark:text-[#ccd6f6] leading-snug'>
                        {study.outcomeHeadline}
                      </p>
                      <h4 className='text-xl font-bold text-gray-800 dark:text-gray-300 mt-4'>{study.title}</h4>
                      <p className='text-pink-600 dark:text-pink-400 font-medium mt-1'>{study.subtitle}</p>
                    </div>
                  </div>
                  <div className='p-6 md:p-8 pt-2 md:pt-2 grid gap-8 md:grid-cols-2 border-t border-gray-200 dark:border-gray-700'>
                    <div>
                      <h5 className='text-xs font-semibold uppercase tracking-wider text-pink-600 mb-2'>The need</h5>
                      <p className='text-gray-700 dark:text-gray-300 text-sm leading-relaxed'>{study.problem}</p>
                    </div>
                    <div>
                      <h5 className='text-xs font-semibold uppercase tracking-wider text-pink-600 mb-2'>What we built</h5>
                      <p className='text-gray-700 dark:text-gray-300 text-sm leading-relaxed'>{study.solution}</p>
                    </div>
                  </div>
                  <div className='px-6 md:px-8 pb-8 grid gap-8 lg:grid-cols-3'>
                    <div className='lg:col-span-1'>
                      <h5 className='text-xs font-semibold uppercase tracking-wider text-pink-600 mb-3'>
                        At a glance
                      </h5>
                      <ul className='space-y-2'>
                        {study.keyFeatures.map((item) => (
                          <li key={item} className='flex gap-2 text-sm text-gray-700 dark:text-gray-300'>
                            <HiCheck className='text-pink-600 shrink-0 mt-0.5' aria-hidden />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h5 className='text-xs font-semibold uppercase tracking-wider text-pink-600 mb-3'>Tech stack</h5>
                      <ul className='flex flex-wrap gap-2'>
                        {study.techStack.map((tech) => (
                          <li
                            key={tech}
                            className='text-xs font-medium px-3 py-1 rounded-full bg-white dark:bg-[#112240] border border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-200'
                          >
                            {tech}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className='lg:col-span-1'>
                      <h5 className='text-xs font-semibold uppercase tracking-wider text-pink-600 mb-2'>
                        Outcome
                      </h5>
                      <p className='text-sm leading-relaxed text-gray-700 dark:text-gray-300 border-l-4 border-pink-600 pl-4 py-1'>
                        {study.result}
                      </p>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default Work;
